/**
 * Module: ProductScreen
 * Role: Product detail overlay at /product/:offerId — wired to
 *       GET /api/v2/buyer/tasks/{offer_id}.
 *       On "Беру" tap: calls POST /api/v2/buyer/slots/take, then navigates
 *       to /flow/{slot_id} (standard) or /pantene/{slot_id}/upload (is_eta2).
 *
 * States:
 *   isPending → Skeleton placeholders
 *   isError   → honest EmptyState ("Товар недоступен") — incl. flag-off 404
 *   slots_available === 0 → "Мест нет" state, no "Беру" CTA
 *   slots_available >  0  → product content + sticky "Беру" PressBtn
 *
 * Uses:
 *   - components/atoms: IOSScreen, PageHeader, Card, ProductImg, Tag,
 *                       GlassPill, Ic, EmptyState, Skeleton, ScrollEdgeBottom, PressBtn
 *   - @tanstack/react-query: useQuery, useMutation, useQueryClient
 *   - api/client: apiFetch
 *   - hooks/usePlatformBack: TG BackButton navigate(-1) registration
 *   - react-router-dom: useParams, useNavigate
 *   - styles/tokens: P, S, T
 *   - lib/format: formatKopecks
 *
 * Used by:
 *   - App.tsx (route /product/:offerId, sibling of AppShell)
 *
 * Glossary: ai/glossary/buyer.md
 */
import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import {
  IOSScreen,
  PageHeader,
  Card,
  ProductImg,
  Tag,
  GlassPill,
  Ic,
  EmptyState,
  Skeleton,
  ScrollEdgeBottom,
  PressBtn,
} from '../components/atoms';
import { usePlatformBack } from '../hooks/usePlatformBack';
import { P, S, T } from '../styles/tokens';
import { formatKopecks } from '../lib/format';

// ─── Backend contract (planner-verified, field names exact) ──────────────────
// L-240: inline interface — NEVER import from api/types.ts (stale placeholder).
// Verified against src/api/v2/buyer/_tasks_schemas.py TaskDetail.
interface TaskDetail {
  offer_id: string;           // UUID as string
  campaign_id: string;        // UUID as string
  product_title: string;
  product_image_url: string | null;
  cashback_kopecks: number;   // int kopecks (ADR-001)
  marketplace: string;        // buyer-facing label: "WB" | "Ozon"
  slots_available: number;
  schedule_date: string;      // ISO date "YYYY-MM-DD"
  keyword: string | null;
  product_price_kopecks: number | null;
  slot_price_kopecks: number; // returned by API; not rendered to buyer (BUG-1102)
  ugc_photo_count: number;
  ugc_video_count: number;
  is_eta2: boolean;
  // SYMMETRIC LIMIT — sync with:
  //   - src/api/v2/buyer/_tasks_schemas.py TaskDetail.default_ugc_type
  //   - src/api/v2/buyer/tasks.py _derive_default_ugc_type
  default_ugc_type: 'text' | 'photo' | 'video';
  // SYMMETRIC LIMIT (ADR-022, BUG-1108) — has_flow enforcement points:
  //   - src/api/v2/buyer/_tasks_schemas.py TaskDetail.has_flow (Pydantic)
  //   - src/api/v2/buyer/tasks.py get_task_detail_impl builder (bool cast)
  //   - this FE interface (route to flow screen ONLY when true)
  has_flow: boolean;
  // ADR-040 explicit payment model marker — sync with:
  //   - src/api/v2/buyer/_tasks_schemas.py TaskItem.payment_model
  //   - miniapp/src/api/tasks.ts TaskItem.payment_model
  //   - miniapp/src/screens/TasksScreen.tsx (cashback label branch)
  //   - miniapp/src/screens/HomeScreen/RecommendationsList.tsx (cashback label branch)
  payment_model?: 'fixed' | 'by_receipt' | null;
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function ProductSkeleton() {
  return (
    <div style={{ padding: '14px 16px 16px' }}>
      {/* Hero image skeleton */}
      <Skeleton variant="block" h={280} style={{ borderRadius: 24, marginBottom: 16 }} />
      {/* Tag pill */}
      <Skeleton h={26} w={60} style={{ borderRadius: 999, marginBottom: 10 }} />
      {/* Title */}
      <Skeleton h={28} w="90%" style={{ marginBottom: 8 }} />
      <Skeleton h={28} w="70%" style={{ marginBottom: 14 }} />
      {/* Cashback caveat row */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 22 }}>
        <Skeleton h={38} w={120} />
        <Skeleton h={16} w={60} />
      </div>
      {/* Pre-task cream block */}
      <Skeleton variant="block" h={110} style={{ borderRadius: 16 }} />
    </div>
  );
}

// ─── Slot take types ─────────────────────────────────────────────────────────

type TakePayload = { offer_id: string; ugc_type: string };
type SlotTaken = {
  slot_id: string;
  status: string;
  ugc_type: string;
  reward_kopecks: number;
  deadline_at: string;
  idempotent_replay: boolean;
};

// ─── ProductScreen ────────────────────────────────────────────────────────────

export default function ProductScreen() {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Register TG BackButton → navigate(-1) (EC-3 / L-042).
  // tg.ts fix (Task 1) ensures AppShell's close() handler is offClicked first.
  usePlatformBack();

  const { data, isPending, isError } = useQuery<TaskDetail>({
    queryKey: ['buyer', 'tasks', 'detail', offerId],
    queryFn: () => apiFetch<TaskDetail>(`/api/v2/buyer/tasks/${offerId}`),
    retry: false,
    enabled: Boolean(offerId),
  });

  const takeMutation = useMutation<SlotTaken, Error, TakePayload>({
    mutationFn: (payload) =>
      apiFetch<SlotTaken>('/api/v2/buyer/slots/take', {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyRef.current },
        body: JSON.stringify(payload),
      }),
    onSuccess: (slot) => {
      queryClient.invalidateQueries({ queryKey: ['slots', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['slots', 'active-flows'] });
      queryClient.invalidateQueries({ queryKey: ['buyer', 'tasks'], exact: true });

      // BUG-1108: branch on has_flow BEFORE routing. Legacy slots (campaign has no
      // flow_template_version_id) would 404 on GET /slots/{id}/flow. Honest fallback:
      // task is taken — return to home (it will surface in active tasks). No bot redirect (§7).
      if (!data?.has_flow) {
        navigate('/', { replace: true });
        return;
      }
      const target = data.is_eta2
        ? `/pantene/${slot.slot_id}/upload`
        : `/flow/${slot.slot_id}`;
      navigate(target, { replace: true });
    },
    onError: (err: Error) => {
      const msg = err.message ?? '';
      // BUG-1137 Phase 1: diagnostic telemetry for production debugging.
      console.warn('take_failed', { offer_id: data?.offer_id, message: msg, ts: Date.now() });
      if (msg.includes('ALREADY_TAKEN')) {
        // Idempotent replay or concurrent claim — try to recover slot_id from error message.
        const match = msg.match(/slot[_-]?id["':\s]+([0-9a-f-]{36})/i);
        if (match?.[1]) {
          navigate(`/flow/${match[1]}`, { replace: true });
        } else {
          // BUG-1137 H3: no slot_id in message — guide buyer to active tasks
          // and ensure ActiveTaskPill picks up the assigned slot.
          queryClient.invalidateQueries({ queryKey: ['slots', 'active'] });
          setInlineError('Задание уже взято — проверь «Активные задания»');
        }
      } else if (msg.includes('OFFER_UNAVAILABLE')) {
        // BUG-1137 H2: was silent — invalidate caused CTA to vanish with no explanation.
        setInlineError('Все места разобраны, обнови список');
        queryClient.invalidateQueries({ queryKey: ['buyer', 'tasks', 'detail', offerId] });
      } else if (msg.includes('ONBOARDING_NOT_COMPLETED')) {
        // BUG-1132: server rejected take — buyer hasn't completed onboarding.
        navigate('/onboarding', { replace: true });
      } else if (msg.includes('API 403')) {
        setInlineError('Сейчас недоступно');
      } else {
        setInlineError('Не удалось взять задание, попробуй ещё раз');
      }
    },
  });

  return (
    <IOSScreen>
      <PageHeader
        onBack={() => navigate(-1)}
        right={
          !isPending && !isError && data !== undefined && data.slots_available > 0 ? (
            <GlassPill variant="warmTag" size={36}
              style={{ padding: '0 12px', gap: 6, color: P.warn, font: T.footnote, fontWeight: 700 }}
            >
              <Ic name="clock" size={14} color={P.warn} />
              Осталось {data.slots_available}
            </GlassPill>
          ) : undefined
        }
      />

      {/* ── Loading state (EC-4) ── */}
      {isPending && <ProductSkeleton />}

      {/* ── Error / flag-off state (EC-5): honest EmptyState, no crash ── */}
      {isError && !isPending && (
        <EmptyState
          title="Товар недоступен"
          subtitle="Попробуй позже"
        />
      )}

      {/* ── Success state ── */}
      {!isPending && !isError && data !== undefined && (
        <>
          <div style={{ padding: '14px 16px 0' }}>
            {/* Hero image — real URL or procedural gradient fallback */}
            {data.product_image_url ? (
              <img
                src={data.product_image_url}
                alt={data.product_title}
                style={{
                  width: '100%',
                  height: 280,
                  objectFit: 'cover',
                  borderRadius: 24,
                  display: 'block',
                }}
              />
            ) : (
              <ProductImg
                h={280}
                w="100%"
                r={24}
                hue={data.product_title.charCodeAt(0) % 360}
              />
            )}

            {/* Marketplace tag + product title */}
            <div style={{ marginTop: 16 }}>
              <Tag style={{ font: T.subhead, fontWeight: 600, padding: '4px 10px' }}>
                {data.marketplace}
              </Tag>
              <p
                style={{
                  font: T.title1,
                  color: S.label,
                  margin: '10px 0 0',
                  lineHeight: 1.2,
                }}
              >
                {data.product_title}
              </p>
            </div>

            {/* Cashback hero row — by_receipt: "До 100% по чеку"; fixed/legacy: struck price + "+X вернётся" */}
            {data.payment_model === 'by_receipt' ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 12,
                  marginTop: 14,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontFamily: T.fontAccent,
                    fontSize: 38,
                    fontWeight: 700,
                    color: P.terraDark,
                    lineHeight: 1,
                  }}
                >
                  До 100%
                </span>
                <span
                  style={{
                    fontFamily: T.fontAccent,
                    fontSize: 17,
                    fontWeight: 700,
                    color: P.jade,
                    lineHeight: 1,
                    transform: 'rotate(-3deg)',
                    display: 'inline-block',
                  }}
                >
                  по чеку
                </span>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 12,
                  marginTop: 14,
                  flexWrap: 'wrap',
                }}
              >
                {data.product_price_kopecks !== null && (
                  <span
                    style={{
                      font: T.subhead,
                      color: S.secondaryLabel,
                      textDecoration: 'line-through',
                    }}
                  >
                    {formatKopecks(data.product_price_kopecks)}
                  </span>
                )}
                <span
                  style={{
                    fontFamily: T.fontAccent,
                    fontSize: 38,
                    fontWeight: 700,
                    color: P.terraDark,
                    lineHeight: 1,
                  }}
                >
                  +{formatKopecks(data.cashback_kopecks)}
                </span>
                <span
                  style={{
                    fontFamily: T.fontAccent,
                    fontSize: 17,
                    fontWeight: 700,
                    color: P.jade,
                    lineHeight: 1,
                    transform: 'rotate(-3deg)',
                    display: 'inline-block',
                  }}
                >
                  вернётся
                </span>
              </div>
            )}

            {/* Pre-task checklist card — cream background (prototype pattern) */}
            <div
              style={{
                marginTop: 22,
                padding: 16,
                background: P.cream,
                borderRadius: 16,
                font: T.subhead,
                lineHeight: 1.55,
                color: S.label,
              }}
            >
              <p style={{ font: T.headline, margin: '0 0 8px', color: S.label }}>
                Перед тем как взять
              </p>
              {data.product_price_kopecks !== null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Ic name="check" size={14} color={P.success} />
                  У вас есть {formatKopecks(data.product_price_kopecks)} на покупку
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <Ic name="check" size={14} color={P.success} />
                Доставка в ваш город доступна
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <Ic name="check" size={14} color={P.success} />
                Можно оформить прямо сейчас
              </div>
              {/* SYMMETRIC LIMIT (ADR-022, BUG-1170) — deadline copy:
                  - src/api/v2/buyer/_slots_db.py:236 (sla_search_minutes / sla_pickup_minutes)
                  - src/infra/config/timers.py:40-44 (SLA_SEARCH_MINUTES=30)
                  - src/infra/config/timers.py:52-56 (SLA_PICKUP_MINUTES=10080, i.e. 7 days)
                  If SLA values change, update this FE copy. */}
              <div
                style={{
                  marginTop: 12,
                  color: P.warn,
                  font: T.footnote,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Ic name="clock" size={13} color={P.warn} />
                {data.has_flow
                  ? 'После «Беру» — 7 дней на оформление заказа'
                  : 'После «Беру» — 30 минут на оформление заказа'}
              </div>
            </div>

            {(data.keyword || data.product_price_kopecks !== null) && (
              <Card style={{ marginTop: 12 }}>
                {data.keyword && (
                  <div style={{ marginBottom: data.product_price_kopecks !== null ? 10 : 0 }}>
                    <p style={{ font: T.caption, color: S.secondaryLabel, margin: '0 0 2px' }}>
                      Ключевой запрос
                    </p>
                    <p style={{ font: T.subhead, color: S.label, margin: 0 }}>
                      {data.keyword}
                    </p>
                  </div>
                )}
                {data.product_price_kopecks !== null && (
                  <div>
                    <p style={{ font: T.caption, color: S.secondaryLabel, margin: '0 0 2px' }}>
                      Цена товара
                    </p>
                    <p style={{ font: T.subhead, color: S.label, margin: 0 }}>
                      {formatKopecks(data.product_price_kopecks)}
                    </p>
                  </div>
                )}
              </Card>
            )}

            {/* EC-7: no slots → honest "Мест нет", no CTA */}
            {data.slots_available === 0 && (
              <div style={{ marginTop: 16 }}>
                <EmptyState title="Мест нет" subtitle="Все слоты заняты — проверь позже" />
              </div>
            )}

            <div style={{ height: 120 }} />
          </div>

          {/* sticky "Беру" CTA — only when slots_available > 0 */}
          {data.slots_available > 0 && (
            <ScrollEdgeBottom>
              {inlineError && (
                <div
                  role="alert"
                  style={{
                    color: P.terraDark,
                    font: T.caption,
                    textAlign: 'center',
                    marginBottom: 8,
                    padding: '0 16px',
                  }}
                >
                  {inlineError}
                </div>
              )}
              <PressBtn
                bg={P.terracotta}
                onClick={() => takeMutation.mutate({
                  offer_id: data.offer_id,
                  ugc_type: data.default_ugc_type,
                })}
                disabled={takeMutation.isPending}
              >
                {takeMutation.isPending ? (
                  'Беру...'
                ) : (
                  <>
                    Беру <Ic name="arrowUR" size={16} color="#fff" />
                  </>
                )}
              </PressBtn>
            </ScrollEdgeBottom>
          )}
        </>
      )}
    </IOSScreen>
  );
}
